/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 19-05-2026
 *
 * Modal-inhoud voor het laden van een model. Vervangt de AngularJS modalloadmenu.
 * Wordt geopend via ModelbeheerService.openModelLoader() met BsModalService.
 */
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DateTime } from 'luxon';
import type { ModelKeuze, ModelSamenvatting } from './model-loader.types';
import type { ModelId } from '../../../app/backend/api.service';
import { BaseIslandComponent } from '../../base-island.component';

@Component({
    selector: 'dl-model-loader',
    standalone: true,
    templateUrl: './dl-model-loader.component.html',
    styleUrls: ['./dl-model-loader.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, TranslatePipe, FormsModule],
})
export class DlModelLoaderComponent extends BaseIslandComponent implements OnInit {

    /**
     * Referentie naar de modal-instantie, geïnjecteerd door BsModalService.
     */
    readonly bsModalRef = inject(BsModalRef);

    constructor() { super(); }

    // --- Properties (ingesteld door BsModalService via initialState) ---

    /** Lijst van beschikbare modellen. */
    modellen: ModelSamenvatting[] = [];

    /** ID van het huidige model (wordt uitgefilterd). */
    huidigModelId: ModelId | null = null;

    /** Of de modal in meekijkmodus staat. */
    meekijken = false;

    // --- Properties ---

    /** De gefilterde modellen (zonder het huidige model). */
    beschikbareModellen: ModelSamenvatting[] = [];

    /** Het geselecteerde model. */
    geselecteerdModel: ModelSamenvatting | null = null;

    /** Het model dat gemarkeerd is voor verwijdering. */
    teVerwijderenModel: ModelSamenvatting | null = null;

    /** De huidige filterwaarde. */
    filter = '';

    /**
     * Het resultaat dat teruggegeven wordt aan de aanroeper.
     * null = geannuleerd, false = nieuw model, ModelSamenvatting = model laden.
     */
    resultaat: ModelKeuze = null;

    ngOnInit(): void {
        this.beschikbareModellen = this.modellen.filter(m => m.id !== this.huidigModelId);
        this.cdr.markForCheck();
    }

    // --- Public methods ---

    /**
     * Geeft de gefilterde modellen terug op basis van de huidige filterwaarde.
     */
    get gefilterdeModellen(): ModelSamenvatting[] {
        if (!this.filter?.length) {
            return this.beschikbareModellen.filter(m => !m.inDelete);
        }
        const woorden = this.filter.toLowerCase().split(/\s+/);
        return this.beschikbareModellen.filter(m => {
            if (m.inDelete) return false;
            for (const woord of woorden) {
                if (
                    m.titel.toLowerCase().indexOf(woord) === -1 &&
                    (!m.owner || m.owner.toLowerCase().indexOf(woord) === -1)
                ) {
                    return false;
                }
            }
            return true;
        });
    }

    /**
     * Zet een ISO-datumstring om naar een leesbare Nederlandse datum (DD-MM-YYYY).
     */
    datumNaarNl(datum: string): string {
        return DateTime.fromISO(datum).toLocal().toFormat('dd-MM-yyyy');
    }

    /**
     * Klik op een modelregel: selecteer het model. Bij dubbelklik ook direct laden.
     */
    klikModel(model: ModelSamenvatting, laden: boolean): void {
        this.teVerwijderenModel = null;
        if (model.inDelete) return;
        if (laden) {
            this.resultaat = model;
            this.bsModalRef.hide();
            return;
        }
        this.geselecteerdModel = model;
        this.cdr.markForCheck();
    }

    /**
     * Klik op de verwijderknop van een model. Eerste klik markeert, tweede klik verwijdert.
     * Verwijdering wordt afgehandeld via de verwijderCallback die door de aanroeper is meegegeven.
     */
    verwijderModel(model: ModelSamenvatting): void {
        if (this.meekijken) return;
        if (this.teVerwijderenModel === model) {
            model.inDelete = true;
            this.teVerwijderenModel = null;
            this.verwijderCallback?.(model.id).catch(() => {
                model.inDelete = false;
                this.cdr.markForCheck();
            });
        } else {
            this.teVerwijderenModel = model;
        }
        this.cdr.markForCheck();
    }

    /**
     * Callback voor het verwijderen van een model, ingesteld door de aanroeper.
     */
    verwijderCallback?: (modelId: ModelId) => Promise<unknown>;

    /**
     * Laad het geselecteerde model en sluit de modal.
     */
    laadModel(): void {
        if (!this.geselecteerdModel) return;
        this.resultaat = this.geselecteerdModel;
        this.bsModalRef.hide();
    }

    /**
     * Start een nieuw model en sluit de modal.
     */
    nieuwModel(): void {
        this.resultaat = false;
        this.bsModalRef.hide();
    }

    /**
     * Annuleer en sluit de modal zonder resultaat.
     */
    annuleer(): void {
        this.resultaat = null;
        this.bsModalRef.hide();
    }
}
